import styles from 'src/pages/PaymentBetaPage/components/StripeElements/StripeElements.module.scss';
import {
  ExpressCheckoutElement,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import cn from 'classnames';
import {
  useColorTheme,
  useSession,
  useVisible,
  useManageCreditAccountPageRightPanel,
} from 'src/hooks';
import { FormEvent, useEffect, useState } from 'react';
import {
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementConfirmEvent,
  StripePaymentElementChangeEvent,
} from '@stripe/stripe-js';
import { getNanosPrice, sendGTMEvent } from 'src/utils';
import { AppRoutes, GTMEvent, UserSettingsNestedRoutes } from 'src/types';
import {
  CREDITS_ADDED_SUCCESS_MESSAGE,
  PAYMENT_ERROR_MESSAGE,
} from 'src/constants';
import { env } from 'src/env';
import { toast } from 'react-toastify';
import { useCreateCreditAccountMutation } from 'src/store/services';
import { CreditAccountPaymentSource } from 'src/types/wallet/models/CreditAccountPaymentSource';

type StripeElementsProps = {
  onReady: () => void;
  onPaymentProcessing: (value: boolean) => void;
  disablePayment?: boolean;
  amount: number;
  thresholdAmount: number;
};

export const StripeElements = ({
  onReady,
  onPaymentProcessing,
  disablePayment,
  amount,
  thresholdAmount,
}: StripeElementsProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const {
    appUser: { user_id, email },
  } = useSession();
  const { changeActivePanelView } = useManageCreditAccountPageRightPanel();
  const [isReadyPaymentElement, setReadyPaymentElement] =
    useState<boolean>(false);
  const [isCashAppButton, setIsCashAppButton] = useState<boolean>(false);

  const {
    isVisible: isSubmitDisabled,
    onVisibilitySet: setSubmitDisabled,
    onVisibilityRemove: setSubmitEnabled,
  } = useVisible(true);

  const [createAccount] = useCreateCreditAccountMutation();
  const { isDarkTheme } = useColorTheme();

  useEffect(() => {
    if (isReadyPaymentElement) {
      onReady();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReadyPaymentElement]);

  const handlePaymentElementChange = ({
    complete,
    value,
    collapsed,
  }: StripePaymentElementChangeEvent) => {
    if (value.type === 'cashapp') {
      setIsCashAppButton(true);
    } else {
      setIsCashAppButton(false);
    }

    if (complete) {
      setSubmitEnabled();
    } else {
      setSubmitDisabled();
    }

    // !!! (olha): if we change defaultCollapsed to false, GTM event don't work properly
    if (!collapsed) {
      sendGTMEvent(GTMEvent.PAYMENT_METHOD_CLICK, {
        payment_method_type: value.type,
      });
    }
  };

  const handleExpressCheckoutClick = (
    event: StripeExpressCheckoutElementClickEvent,
  ) => {
    sendGTMEvent(GTMEvent.PAYMENT_METHOD_CLICK, {
      payment_method_type: event.expressPaymentType,
    });
    event.resolve();
  };

  const handleSubmitClick = () => {
    sendGTMEvent(GTMEvent.PAYMENT_SUBMIT_BUTTON_CLICK);
  };

  const handleCreateAccount = async (payment_method: string) => {
    try {
      const result = await createAccount({
        user_id,
        paymentInfo: {
          payment_source: CreditAccountPaymentSource.STRIPE,
          payment_config: {
            stripe: {
              use_existing_payment_method: false,
              payment_method_id: payment_method,
            },
          },
          reload_config: {
            auto_reload_enabled: true,
            reload_amount_nanos: getNanosPrice(amount),
            reload_threshold_amount_nanos: getNanosPrice(thresholdAmount),
          },
        },
      }).unwrap();
      sendGTMEvent(GTMEvent.PURCHASE_CREDITS);
      changeActivePanelView(null);
      toast.success(CREDITS_ADDED_SUCCESS_MESSAGE);
      onPaymentProcessing(false);
      return result;
    } catch (error) {
      onPaymentProcessing(false);
      toast.error(PAYMENT_ERROR_MESSAGE);
      return;
    }
  };

  const handleConfirmPayment = async () => {
    if (!elements || !stripe || disablePayment) {
      return;
    }

    const returnUrl = `${env.REACT_APP_NINJA_UI_URL}${AppRoutes.MANAGE_ACCOUNT}/${UserSettingsNestedRoutes.SUBSCRIPTION}`;

    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: returnUrl,
        payment_method_data: {
          billing_details: {
            email,
          },
        },
      },
      redirect: 'if_required',
    });

    if (error) {
      toast.error(PAYMENT_ERROR_MESSAGE);
      onPaymentProcessing(false);
      return;
    }

    if (setupIntent && setupIntent.status === 'succeeded') {
      await handleCreateAccount((setupIntent?.payment_method || '') as string);
    } else {
      toast.error(PAYMENT_ERROR_MESSAGE);
      onPaymentProcessing(false);
    }
  };

  const handleSubmit = async (
    event:
      | StripeExpressCheckoutElementConfirmEvent
      | FormEvent<HTMLFormElement>,
  ) => {
    if ('preventDefault' in event) {
      // We don't want to let default form submission happen here,
      // which would refresh the page.
      event.preventDefault();
    }

    if (!elements || !stripe || disablePayment) {
      return;
    }

    onPaymentProcessing(true);

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit();
    if (submitError) {
      onPaymentProcessing(false);
      return;
    }

    await handleConfirmPayment();
  };

  return (
    <form
      className={styles.root}
      onSubmit={handleSubmit}
      data-e2e="stripe-elements-form"
    >
      <ExpressCheckoutElement
        options={{
          paymentMethods: {
            googlePay: 'always',
            link: 'never',
            paypal: 'never',
          },
          paymentMethodOrder: ['apple_pay', 'google_pay', 'amazon_pay'],
          buttonTheme: {
            googlePay: 'white',
            applePay: isDarkTheme ? 'white' : 'black',
          },
          layout: {
            maxColumns: 1,
            maxRows: 5,
          },
          buttonHeight: 53,
        }}
        onConfirm={handleSubmit}
        onClick={handleExpressCheckoutClick}
      />
      <PaymentElement
        options={{
          layout: {
            type: 'accordion',
            // !!! (olha): if we change defaultCollapsed to false, GTM event don't work properly
            defaultCollapsed: true,
            radios: false,
            spacedAccordionItems: true,
          },
          fields: {
            billingDetails: {
              email: 'never',
            },
          },
          paymentMethodOrder: ['google_pay', 'apple_pay', 'card', 'cashapp'],
        }}
        onReady={() => setReadyPaymentElement(true)}
        onChange={handlePaymentElementChange}
      />
      <button
        disabled={isSubmitDisabled}
        className={
          isCashAppButton
            ? cn(styles.submitButton, styles.blackSubmitButton)
            : cn(styles.submitButton, styles.defaultSubmitButton)
        }
        type="submit"
        onClick={handleSubmitClick}
        data-e2e="subscribe-submit-button"
      >
        Confirm & pay
      </button>
    </form>
  );
};
